import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { Alert } from '@/components/ui/Alert/Alert';
import { Spinner } from '@/components/ui/Spinner';
import { LocationAutocomplete, type LocationData } from '@/components/ui/LocationAutocomplete/LocationAutocomplete';
import { useNotificationStore } from '@/stores/notificationStore';
import { 
  MapPin, 
  Search, 
  Hotel, 
  Star, 
  Phone, 
  Globe, 
  Navigation,
  CircleDot,
  Type,
  Map as MapIcon,
  CheckCircle2,
  Database,
  Download,
  Square,
  CheckSquare,
  Loader2,
  Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  googlePlacesSearchService, 
  type HotelSearchResult, 
  type HotelSearchResponse,
  type SearchLocation,
  type ExistingVenueInfo,
  type HotelImportData
} from '@/services/GooglePlacesSearchService';
import { VenuesService } from '@/services/VenuesService';

interface GooglePlacesImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete?: () => void;
}

type SearchMode = 'text' | 'radius';

export function GooglePlacesImportModal({ isOpen, onClose, onImportComplete }: GooglePlacesImportModalProps) {
  const { push: addNotification } = useNotificationStore();
  
  // Location state
  const [locationSearch, setLocationSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lon: number;
    name: string;
  } | null>(null);
  
  // Search mode
  const [searchMode, setSearchMode] = useState<SearchMode>('text');
  
  // Radius for search (in km for display, converted to meters for API)
  const [radiusKm, setRadiusKm] = useState(15);
  
  // Search results
  const [results, setResults] = useState<HotelSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Loading progress for auto-pagination
  const [loadingProgress, setLoadingProgress] = useState<string>('');
  const [pagesLoaded, setPagesLoaded] = useState(0);
  
  // Existing venues tracking (placeId -> venue info)
  const [existingVenues, setExistingVenues] = useState<Record<string, ExistingVenueInfo>>({});
  
  // Selection state
  const [selectedHotels, setSelectedHotels] = useState<Set<string>>(new Set());
  
  // Import state
  const [isImporting, setIsImporting] = useState(false);
  
  // AI Research option
  const [triggerAiResearch, setTriggerAiResearch] = useState(true);

  // Map container ref for the embed
  const mapContainerRef = useRef<HTMLDivElement>(null);
  
  // Services
  const venuesService = new VenuesService();
  
  // Abort controller for search
  const abortControllerRef = useRef<AbortController | null>(null);

  // Get Google Maps API key from environment
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // Compute derived values
  const newHotels = results.filter(h => !existingVenues[h.placeId]);
  const selectedCount = selectedHotels.size;
  const allNewSelected = newHotels.length > 0 && newHotels.every(h => selectedHotels.has(h.placeId));

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Cancel any ongoing search
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setLocationSearch('');
      setSelectedLocation(null);
      setResults([]);
      setSearchError(null);
      setHasSearched(false);
      setExistingVenues({});
      setSelectedHotels(new Set());
      setLoadingProgress('');
      setPagesLoaded(0);
    }
  }, [isOpen]);

  // Handle location selection from autocomplete
  const handleLocationSelect = useCallback((location: LocationData) => {
    setSelectedLocation({
      lat: location.lat,
      lon: location.lon,
      name: location.city || location.country || location.formatted_address || 'Selected Location',
    });
    setSearchError(null);
    // Clear previous results when location changes
    setResults([]);
    setHasSearched(false);
    setSelectedHotels(new Set());
    setExistingVenues({});
  }, []);

  // Perform the search with auto-pagination
  const performSearch = useCallback(async () => {
    if (!selectedLocation) {
      setSearchError('Please select a location first');
      return;
    }

    // Cancel any ongoing search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsSearching(true);
    setResults([]);
    setExistingVenues({});
    setSelectedHotels(new Set());
    setSearchError(null);
    setLoadingProgress('Starting search...');
    setPagesLoaded(0);

    const allResults: HotelSearchResult[] = [];
    let pageToken: string | undefined = undefined;
    let pageCount = 0;

    try {
      const location: SearchLocation = {
        lat: selectedLocation.lat,
        lon: selectedLocation.lon,
        radiusMeters: radiusKm * 1000,
      };

      if (searchMode === 'radius') {
        // Radius/nearby search - single request, no pagination
        setLoadingProgress('Searching nearby hotels...');
        const response = await googlePlacesSearchService.searchNearbyHotels({
          location,
          maxResults: 20,
          rankBy: 'POPULARITY',
        });
        allResults.push(...response.hotels);
        pageCount = 1;
      } else {
        // Text search - auto-paginate through all results
        const query = googlePlacesSearchService.buildHotelQuery(selectedLocation.name);
        
        do {
          pageCount++;
          setLoadingProgress(`Loading page ${pageCount}...`);
          setPagesLoaded(pageCount);
          
          const response = await googlePlacesSearchService.searchHotels({
            query,
            location,
            pageSize: 20,
            pageToken,
          });
          
          allResults.push(...response.hotels);
          setResults([...allResults]); // Update results incrementally
          
          pageToken = response.nextPageToken;
          
          // Small delay between pages to avoid rate limiting
          if (pageToken) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        } while (pageToken);
      }

      setResults(allResults);
      setHasSearched(true);
      setLoadingProgress('');

      // Check which hotels already exist in our database
      if (allResults.length > 0) {
        setLoadingProgress('Checking existing venues...');
        const placeIds = allResults.map(h => h.placeId).filter(Boolean);
        try {
          const { existing } = await googlePlacesSearchService.checkExistingVenues(placeIds);
          setExistingVenues(existing);
        } catch (checkError) {
          console.warn('Failed to check existing venues:', checkError);
        }
        setLoadingProgress('');
      }

    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Hotel search failed:', error);
        setSearchError(error instanceof Error ? error.message : 'Search failed');
      }
    } finally {
      setIsSearching(false);
      setLoadingProgress('');
    }
  }, [selectedLocation, searchMode, radiusKm]);

  // Toggle selection for a single hotel
  const toggleHotelSelection = useCallback((placeId: string) => {
    setSelectedHotels(prev => {
      const next = new Set(prev);
      if (next.has(placeId)) {
        next.delete(placeId);
      } else {
        next.add(placeId);
      }
      return next;
    });
  }, []);

  // Select/deselect all new hotels
  const toggleSelectAll = useCallback(() => {
    if (allNewSelected) {
      // Deselect all
      setSelectedHotels(new Set());
    } else {
      // Select all new hotels
      setSelectedHotels(new Set(newHotels.map(h => h.placeId)));
    }
  }, [allNewSelected, newHotels]);

  // Import selected hotels
  const handleImport = useCallback(async (importAll: boolean) => {
    const hotelsToImport = importAll 
      ? newHotels 
      : results.filter(h => selectedHotels.has(h.placeId) && !existingVenues[h.placeId]);

    if (hotelsToImport.length === 0) {
      addNotification({
        type: 'warning',
        title: 'No Hotels to Import',
        message: importAll ? 'All hotels are already in the database' : 'Please select hotels to import'
      });
      return;
    }

    setIsImporting(true);
    try {
      const importData: HotelImportData[] = hotelsToImport.map(h => ({
        placeId: h.placeId,
        name: h.name,
        address: h.address,
        lat: h.lat,
        lon: h.lon,
        rating: h.rating,
        userRatingCount: h.userRatingCount,
        phone: h.phone,
        website: h.website,
      }));

      const result = await googlePlacesSearchService.importHotels(importData);

      // Trigger AI research if enabled and we have imported venues
      if (triggerAiResearch && result.importedIds.length > 0) {
        try {
          await venuesService.bulkAction('enrich', {
            allFiltered: false,
            ids: result.importedIds,
          });
          addNotification({
            type: 'success',
            title: 'Import & Research Started',
            message: `${result.imported} hotel${result.imported !== 1 ? 's' : ''} imported. AI research started for all.`
          });
        } catch (researchError) {
          // Import succeeded but research failed - still show success with warning
          console.error('AI research trigger failed:', researchError);
          addNotification({
            type: 'success',
            title: 'Import Complete',
            message: result.message
          });
          addNotification({
            type: 'warning',
            title: 'AI Research Failed to Start',
            message: 'Hotels were imported but AI research could not be triggered automatically'
          });
        }
      } else {
        addNotification({
          type: 'success',
          title: 'Import Complete',
          message: result.message
        });
      }

      // Update existing venues map with newly imported ones
      const newExisting = { ...existingVenues };
      hotelsToImport.forEach(h => {
        newExisting[h.placeId] = { id: '', property_name: h.name };
      });
      setExistingVenues(newExisting);
      setSelectedHotels(new Set());

      // Notify parent to refresh
      onImportComplete?.();

    } catch (error) {
      console.error('Import failed:', error);
      addNotification({
        type: 'error',
        title: 'Import Failed',
        message: error instanceof Error ? error.message : 'Failed to import hotels'
      });
    } finally {
      setIsImporting(false);
    }
  }, [newHotels, results, selectedHotels, existingVenues, addNotification, onImportComplete, triggerAiResearch, venuesService]);

  // Build map URL for the embed
  const getMapUrl = (): string => {
    if (!googleMapsApiKey || !selectedLocation) return '';
    return `https://www.google.com/maps/embed/v1/view?key=${googleMapsApiKey}&center=${selectedLocation.lat},${selectedLocation.lon}&zoom=13`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Hotels from Google Places"
      description="Search for hotels using Google Places and import them into your database"
      size="xl"
    >
      <div className="space-y-6">
        {/* Step 1: Location Selection */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
              1
            </div>
            <span>Select Location</span>
          </div>
          
          <div className="relative min-h-[44px]">
            <LocationAutocomplete
              value={locationSearch}
              onChange={setLocationSearch}
              onLocationSelect={handleLocationSelect}
              placeholder="Search for a city or area..."
              types="cities"
            />
          </div>

          {selectedLocation && (
            <div className="flex items-center gap-2 p-3 bg-primary-50 rounded-lg border border-primary-200">
              <MapPin className="w-5 h-5 text-primary-600" />
              <div className="flex-1">
                <div className="font-medium text-primary-900">{selectedLocation.name}</div>
                <div className="text-sm text-primary-600">
                  {selectedLocation.lat.toFixed(6)}, {selectedLocation.lon.toFixed(6)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Map Display */}
        {selectedLocation && googleMapsApiKey && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapIcon className="w-4 h-4" />
              <span>Search area preview</span>
            </div>
            <div 
              ref={mapContainerRef}
              className="relative rounded-lg overflow-hidden border border-gray-200"
              style={{ height: '180px' }}
            >
              <iframe
                width="100%"
                height="100%"
                style={{ border: 0, display: 'block' }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={getMapUrl()}
                title="Search location map"
              />
              <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-md shadow-sm flex items-center gap-2 text-sm">
                <CircleDot className="w-4 h-4 text-primary-600" />
                <span className="text-gray-700">
                  Radius: <span className="font-medium">{radiusKm} km</span>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Search Configuration */}
        {selectedLocation && (
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                2
              </div>
              <span>Configure Search</span>
            </div>

            {/* Search Mode Toggle */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSearchMode('text')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all',
                  searchMode === 'text'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                )}
              >
                <Type className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-medium">Text Search</div>
                  <div className="text-xs opacity-75">All pages auto-loaded</div>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setSearchMode('radius')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all',
                  searchMode === 'radius'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                )}
              >
                <CircleDot className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-medium">Radius Search</div>
                  <div className="text-xs opacity-75">Max 20 results</div>
                </div>
              </button>
            </div>

            {/* Radius Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Search Radius</span>
                <span className="font-medium text-gray-900">{radiusKm} km</span>
              </div>
              <input
                type="range"
                min={1}
                max={50}
                step={1}
                value={radiusKm}
                onChange={(e) => setRadiusKm(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>1 km</span>
                <span>50 km</span>
              </div>
            </div>

            {/* Search Button */}
            <Button
              onClick={performSearch}
              disabled={isSearching}
              className="w-full"
              size="lg"
              leftIcon={isSearching ? <Spinner size="sm" /> : <Search className="w-5 h-5" />}
            >
              {isSearching ? loadingProgress || 'Searching...' : 'Search Hotels'}
            </Button>
          </div>
        )}

        {/* Error Display */}
        {searchError && (
          <Alert variant="error">
            {searchError}
          </Alert>
        )}

        {/* Step 3: Results */}
        {hasSearched && (
          <div className="space-y-4 pt-4 border-t border-gray-200">
            {/* Results Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                  3
                </div>
                <span>Results</span>
                {pagesLoaded > 1 && (
                  <span className="text-xs text-gray-500">({pagesLoaded} pages)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {Object.keys(existingVenues).length > 0 && (
                  <Badge variant="success" className="text-xs">
                    {Object.keys(existingVenues).length} in database
                  </Badge>
                )}
                {newHotels.length > 0 && (
                  <Badge variant="warning" className="text-xs">
                    {newHotels.length} new
                  </Badge>
                )}
                <Badge variant="secondary">
                  {results.length} total
                </Badge>
              </div>
            </div>

            {/* Selection Controls */}
            {newHotels.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
                >
                  {allNewSelected ? (
                    <CheckSquare className="w-5 h-5 text-primary-600" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-400" />
                  )}
                  <span>
                    {allNewSelected ? 'Deselect All' : `Select All New (${newHotels.length})`}
                  </span>
                </button>
                {selectedCount > 0 && (
                  <Badge variant="primary">{selectedCount} selected</Badge>
                )}
              </div>
            )}

            {/* Results List */}
            {results.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Hotel className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No hotels found</p>
                <p className="text-sm mt-1">Try expanding your search radius or changing the location</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[350px] overflow-y-auto pr-2">
                {results.map((hotel, index) => (
                  <HotelResultCard 
                    key={`${hotel.placeId}-${index}`} 
                    hotel={hotel} 
                    existingVenue={existingVenues[hotel.placeId]}
                    isSelected={selectedHotels.has(hotel.placeId)}
                    onToggleSelect={() => toggleHotelSelection(hotel.placeId)}
                  />
                ))}
              </div>
            )}

            {/* Import Actions */}
            {newHotels.length > 0 && (
              <div className="space-y-3 pt-2">
                {/* AI Research Toggle */}
                <label className="flex items-center gap-3 p-3 bg-primary-50 rounded-lg border border-primary-200 cursor-pointer hover:bg-primary-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={triggerAiResearch}
                    onChange={(e) => setTriggerAiResearch(e.target.checked)}
                    className="w-4 h-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <Sparkles className="w-5 h-5 text-primary-600" />
                  <div className="flex-1">
                    <div className="font-medium text-primary-900 text-sm">Trigger AI Research</div>
                    <div className="text-xs text-primary-600">Automatically start AI research on imported hotels</div>
                  </div>
                </label>

                {/* Import Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleImport(false)}
                    disabled={isImporting || selectedCount === 0}
                    leftIcon={isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    className="flex-1"
                  >
                    {isImporting ? 'Importing...' : `Import Selected (${selectedCount})`}
                  </Button>
                  <Button
                    onClick={() => handleImport(true)}
                    disabled={isImporting}
                    leftIcon={isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    className="flex-1"
                  >
                    {isImporting ? 'Importing...' : `Import All New (${newHotels.length})`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Individual hotel result card with selection
 */
function HotelResultCard({ 
  hotel, 
  existingVenue,
  isSelected,
  onToggleSelect
}: { 
  hotel: HotelSearchResult; 
  existingVenue?: ExistingVenueInfo;
  isSelected: boolean;
  onToggleSelect: () => void;
}) {
  const isInDatabase = !!existingVenue;
  
  return (
    <div 
      className={cn(
        "p-3 border rounded-lg transition-all",
        isInDatabase 
          ? "bg-success-50 border-success-200" 
          : isSelected
            ? "bg-primary-50 border-primary-300 ring-1 ring-primary-300"
            : "bg-white border-gray-200 hover:border-gray-300"
      )}
    >
      <div className="flex items-start gap-3">
        {/* Selection Checkbox or Status Icon */}
        <div className="flex-shrink-0 pt-0.5">
          {isInDatabase ? (
            <div className="w-6 h-6 rounded flex items-center justify-center bg-success-100">
              <CheckCircle2 className="w-4 h-4 text-success-600" />
            </div>
          ) : (
            <button
              type="button"
              onClick={onToggleSelect}
              className="w-6 h-6 rounded flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              {isSelected ? (
                <CheckSquare className="w-5 h-5 text-primary-600" />
              ) : (
                <Square className="w-5 h-5 text-gray-400" />
              )}
            </button>
          )}
        </div>
        
        {/* Hotel Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <h4 className="font-medium text-gray-900 text-sm truncate">{hotel.name}</h4>
              {isInDatabase && (
                <Badge variant="success" className="text-xs flex-shrink-0">
                  <Database className="w-3 h-3 mr-1" />
                  Exists
                </Badge>
              )}
            </div>
            {hotel.rating && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                <span className="text-xs font-medium text-gray-700">{hotel.rating.toFixed(1)}</span>
              </div>
            )}
          </div>
          
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{hotel.address}</p>
          
          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {hotel.website && (
              <a
                href={hotel.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                onClick={e => e.stopPropagation()}
              >
                <Globe className="w-3 h-3" />
                <span>Website</span>
              </a>
            )}
            {hotel.phone && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Phone className="w-3 h-3" />
                <span>{hotel.phone}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
